// 화면 19 — TanStack Query 훅 (폴링 300s, ADR-DASH-06).
import { useQuery } from '@tanstack/react-query';
import { ApiError } from '../../api/client';
import {
  getBandsDistribution,
  getPercentileHistogram,
  getScoreHistory,
  searchStocks,
} from './api';
import type {
  BandsDistribution,
  PercentileHistogram,
  ScoreHistory,
  StockSearchResult,
} from './types';

const POLL_MS = 300_000; // 300s (PRD §5.9.4 — 일 1회 적재)

// search 는 debounced 호출 (폴링 X — autocomplete UX).
export function useStockSearch(query: string) {
  const trimmed = query.trim();
  return useQuery<StockSearchResult, ApiError>({
    queryKey: ['stock-search', trimmed],
    queryFn: () => searchStocks(trimmed),
    enabled: trimmed.length >= 1,
    staleTime: 600_000, // 서버 Caffeine 600s 와 정합.
    // 신규 query 동안 직전 결과 유지 → autocomplete 깜빡임 방지.
    placeholderData: (prev) => prev,
    retry: (failureCount, err) => {
      const status = (err as ApiError | undefined)?.httpStatus;
      if (status && [400, 404].includes(status)) return false;
      return failureCount < 1;
    },
  });
}

export function useScoreHistory(symbol: string | null, days = 90) {
  return useQuery<ScoreHistory, ApiError>({
    queryKey: ['stocks', 'score-history', symbol, days],
    queryFn: () => getScoreHistory(symbol as string, days),
    refetchInterval: POLL_MS,
    staleTime: POLL_MS - 1000,
    enabled: Boolean(symbol),
    retry: (failureCount, err) => {
      const status = (err as ApiError | undefined)?.httpStatus;
      if (status && [400, 404, 422].includes(status)) return false;
      return failureCount < 1;
    },
  });
}

export function useBandsDistribution(days: number) {
  return useQuery<BandsDistribution, ApiError>({
    queryKey: ['stocks', 'band-distribution', days],
    queryFn: () => getBandsDistribution(days),
    refetchInterval: POLL_MS,
    staleTime: POLL_MS - 1000,
    retry: (failureCount, err) => {
      const status = (err as ApiError | undefined)?.httpStatus;
      if (status && [400, 422].includes(status)) return false;
      return failureCount < 1;
    },
  });
}

// date null → 서버 폴백 (max(trading_date)). 휴장 / 미선택 안전.
export function usePercentileHistogram(date: string | null, bins = 10) {
  return useQuery<PercentileHistogram, ApiError>({
    queryKey: ['stocks', 'percentile', date ?? 'latest', bins],
    queryFn: () => getPercentileHistogram(date, bins),
    refetchInterval: POLL_MS,
    staleTime: POLL_MS - 1000,
    retry: (failureCount, err) => {
      const status = (err as ApiError | undefined)?.httpStatus;
      if (status && [400, 404, 422].includes(status)) return false;
      return failureCount < 1;
    },
  });
}
